/**
 * Google reCAPTCHA v3 loader, shared by the newsletter and petition forms.
 *
 * api.js is only loaded on demand (call loadRecaptcha() once the visitor
 * starts typing) and a fresh token is requested at submit time with
 * getToken(), as tokens expire after 2 minutes.
 */

const SCRIPT_ATTR = 'data-recaptcha-v3';
const TOKEN_TIMEOUT_MS = 10000;

// One loader per site key (the newsletter and petition forms may use different keys)
const recaptchaPromises = new Map();

/**
 * Resolves with `(action) => Promise<token>`.
 *
 * grecaptcha.execute(siteKey) only accepts the key api.js was loaded with
 * (`?render=<key>`). When api.js was already loaded with another key (the
 * Salesforce Web-to-Case block, see _partials/forms/_recaptcha.twig), we render
 * an invisible widget for our key instead and execute that.
 */
export const loadRecaptcha = (siteKey) => {
  if (recaptchaPromises.has(siteKey)) {
    return recaptchaPromises.get(siteKey);
  }

  const promise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
    const ownsScript = !existing || existing.src.includes(`render=${encodeURIComponent(siteKey)}`);

    const whenReady = () => window.grecaptcha.ready(() => {
      if (ownsScript) {
        resolve((action) => window.grecaptcha.execute(siteKey, { action }));
        return;
      }

      const container = document.createElement('div');
      container.hidden = true;
      document.body.appendChild(container);
      const widgetId = window.grecaptcha.render(container, { sitekey: siteKey, size: 'invisible' });
      resolve((action) => window.grecaptcha.execute(widgetId, { action }));
    });

    if (window.grecaptcha?.render) {
      whenReady();
      return;
    }

    let script = existing;
    if (!script) {
      script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.setAttribute(SCRIPT_ATTR, '');
      document.head.appendChild(script);
    }

    script.addEventListener('load', whenReady, { once: true });
    script.addEventListener('error', () => {
      recaptchaPromises.delete(siteKey);
      reject(new Error('reCAPTCHA failed to load'));
    }, { once: true });
  });

  recaptchaPromises.set(siteKey, promise);

  return promise;
};

export const getToken = (siteKey, action) => Promise.race([
  loadRecaptcha(siteKey).then((execute) => execute(action)),
  new Promise((_, reject) => window.setTimeout(() => reject(new Error('reCAPTCHA timeout')), TOKEN_TIMEOUT_MS)),
]);
